import { PublicKey } from '@solana/web3.js';
import { BN } from '@coral-xyz/anchor';
import { SOLANA_PROGRAM_ID } from '../../constants';

/**
 * PDA Helper methods for Solana program
 * These methods derive Program Derived Addresses for different accounts
 */

/**
 * Browser-compatible string to Uint8Array
 */
function stringToBytes(str: string): Uint8Array {
  return new TextEncoder().encode(str);
}

/**
 * Safe parse group_id from number/BigInt to BigInt
 * Handles large numbers beyond Number.MAX_SAFE_INTEGER
 * Note: String input is not supported - use number or BigInt directly
 */
function parseGroupIdSafe(groupId: number | bigint): bigint {
  if (typeof groupId === 'bigint') {
    return groupId;
  }
  // Number - convert to BigInt for safe handling
  if (groupId > Number.MAX_SAFE_INTEGER || groupId < Number.MIN_SAFE_INTEGER) {
    // For numbers beyond safe range, we need to handle carefully
    // Since JavaScript number loses precision, we should use BigInt from the start
    return BigInt(Math.floor(groupId));
  }
  return BigInt(groupId);
}

/**
 * Browser-compatible UInt64 LE writer
 * Now supports BigInt for large numbers beyond Number.MAX_SAFE_INTEGER
 * Note: String input is not supported - use number or BigInt directly
 */
function writeUInt64LE(value: number | bigint): Uint8Array {
  const buffer = new Uint8Array(8);
  const view = new DataView(buffer.buffer);
  
  // Parse to BigInt for safe handling of large numbers
  const bigIntValue = parseGroupIdSafe(value);
  
  // Extract low and high 32-bit parts
  const low = Number(bigIntValue & BigInt(0xffffffff));
  const high = Number((bigIntValue >> BigInt(32)) & BigInt(0xffffffff));
  
  view.setUint32(0, low, true); // little endian
  view.setUint32(4, high, true); // little endian
  return buffer;
}

/**
 * Parse string groupId to number or bigint, then convert to number safely
 * Returns number if within safe range, otherwise throws error
 * This is a convenience function for parsing string inputs from API/events
 */
export function parseGroupIdFromString(groupIdStr: string): number | bigint {
  // Try to parse as number first
  const parsed = Number(groupIdStr);
  if (isNaN(parsed)) {
    throw new Error(`Invalid group_id format: ${groupIdStr}. Must be a valid number.`);
  }
  // If exceeds safe integer range, return BigInt
  if (parsed > Number.MAX_SAFE_INTEGER) {
    return BigInt(groupIdStr);
  }
  return parsed;
}

/**
 * Convert group_id to number safely
 * Returns number if within safe range, otherwise throws error
 * Use this when you need number type (e.g., for BN constructor)
 * Note: String input is not supported - use number or BigInt directly
 */
export function groupIdToNumber(groupId: number | bigint): number {
  const bigIntValue = parseGroupIdSafe(groupId);
  
  // Check if value is within safe integer range
  if (bigIntValue > BigInt(Number.MAX_SAFE_INTEGER)) {
    throw new Error(
      `group_id ${bigIntValue.toString()} exceeds Number.MAX_SAFE_INTEGER. ` +
      `Use groupIdToBN() or groupIdToBigInt() instead.`
    );
  }
  
  return Number(bigIntValue);
}

/**
 * Convert group_id to BN (BigNumber from @coral-xyz/anchor)
 * Safe for all u64 values
 * Note: String input is not supported - use number or BigInt directly
 */
export function groupIdToBN(groupId: number | bigint): any {
  const bigIntValue = parseGroupIdSafe(groupId);
  
  // BN can be constructed from string or number
  // Use string to avoid precision loss
  return new BN(bigIntValue.toString());
}

/**
 * Convert group_id to BigInt
 * Safe for all u64 values
 * Note: String input is not supported - use number or BigInt directly
 */
export function groupIdToBigInt(groupId: number | bigint): bigint {
  return parseGroupIdSafe(groupId);
}

export class SolanaPDA {
  private programId: PublicKey;

  constructor(programId?: string) {
    this.programId = new PublicKey(programId || SOLANA_PROGRAM_ID);
  }

  /**
   * Get Global State PDA
   * Seeds: ["global"] - IMPORTANT: seed is 'global', NOT 'global_state'
   */
  getGlobalStatePDA(): [PublicKey, number] {
    return PublicKey.findProgramAddressSync(
      [stringToBytes('global')],
      this.programId,
    );
  }

  /**
   * Get Group State PDA (DEPRECATED)
   * Seeds: ["group", groupId] - IMPORTANT: seed is 'group', NOT 'group_state'
   * 
   * @deprecated This method is deprecated. Use `getGroupStatePDAForAuto(buyer, blockhashHash)` instead.
   * This method was used for the standard PDA pattern [b"group", group_id], but we now exclusively
   * use the buy_tickets_auto pattern [b"group", buyer, blockhash_hash] for all group operations.
   * 
   * This method is kept for backward compatibility and internal use only (e.g., getGroupState for querying).
   * 
   * @param groupId - Can be number or bigint (supports large numbers > Number.MAX_SAFE_INTEGER)
   */
  getGroupStatePDA(groupId: number | bigint): [PublicKey, number] {
    if (process.env.NODE_ENV !== 'production') {
      console.warn(
        '[DEPRECATED] getGroupStatePDA is deprecated. Use getGroupStatePDAForAuto(buyer, blockhashHash) instead. ' +
        'This method is only kept for backward compatibility and internal querying.'
      );
    }
    const groupIdBuffer = writeUInt64LE(groupId);

    return PublicKey.findProgramAddressSync(
      [stringToBytes('group'), groupIdBuffer],
      this.programId,
    );
  }

  /**
   * Get Group State PDA for buy_tickets_auto (PRIMARY METHOD)
   * Seeds: ["group", buyer, blockhash_hash] - FE can derive without calculating group_id
   * This matches the Rust program's BuyTicketsAuto context
   * 
   * This is now the PRIMARY and ONLY method for deriving group state PDAs.
   * All group operations (buyTickets, claimLixi, addWhitelister, etc.) use this pattern.
   * 
   * Uses recent_blockhash (first 4 bytes after hashing) instead of slot for better consistency.
   * recent_blockhash is frozen when transaction is prepared, ensuring FE and Rust use the same value.
   * 
   * @param buyer - Buyer's public key
   * @param blockhashHash - First 4 bytes of hashed recent_blockhash (u32) as string, bigint, or number
   */
  getGroupStatePDAForAuto(buyer: PublicKey, blockhashHash: string | number | bigint): [PublicKey, number] {
    const buyerBytes = buyer.toBytes();
    const blockhashHashBytes = Buffer.allocUnsafe(4); // Changed from 8 to 4 bytes for u32
    // Use DataView for cross-platform compatibility (works in browser and Node.js)
    const dataView = new DataView(blockhashHashBytes.buffer, blockhashHashBytes.byteOffset, blockhashHashBytes.byteLength);
    const blockhashHashNumber = typeof blockhashHash === 'bigint' 
      ? Number(blockhashHash) 
      : typeof blockhashHash === 'string' 
      ? Number(blockhashHash)
      : blockhashHash;
    dataView.setUint32(0, blockhashHashNumber, true); // true = little endian, u32 (4 bytes)

    return PublicKey.findProgramAddressSync(
      [stringToBytes('group'), buyerBytes, blockhashHashBytes],
      this.programId,
    );
  }

  /**
   * Get Jackpot Vault PDA
   * Seeds: ["jackpot_vault"]
   */
  getJackpotVaultPDA(): [PublicKey, number] {
    return PublicKey.findProgramAddressSync(
      [stringToBytes('jackpot_vault')],
      this.programId,
    );
  }

  /**
   * Get NFT Mint PDA for a specific ticket
   * Seeds: ["nft_mint", ticketId]
   */
  getNFTMintPDA(ticketId: number): [PublicKey, number] {
    const ticketIdBuffer = writeUInt64LE(ticketId);

    return PublicKey.findProgramAddressSync(
      [stringToBytes('nft_mint'), ticketIdBuffer],
      this.programId,
    );
  }

  /**
   * Get Extra Token Vault PDA (PRIMARY METHOD)
   * Seeds: ["vault", buyer, blockhash_hash, mint]
   * IMPORTANT: This matches the Rust program's vault authority derivation for buy_tickets_auto flow
   * Pattern changed from [b"vault", group_id, mint] to [b"vault", buyer, blockhash_hash, mint]
   * to allow deriving vault authority BEFORE buy tickets (same as group_state PDA pattern)
   * 
   * @param buyer - Buyer's public key
   * @param blockhashHash - First 4 bytes of hashed recent_blockhash (u32) as string, bigint, or number
   * @param mint - Extra token mint address
   */
  getExtraTokenVaultPDA(
    buyer: PublicKey,
    blockhashHash: string | number | bigint,
    mint: PublicKey,
  ): [PublicKey, number] {
    const buyerBytes = buyer.toBytes();
    const blockhashHashBytes = Buffer.allocUnsafe(4); // u32 = 4 bytes
    // Use DataView for cross-platform compatibility (works in browser and Node.js)
    const dataView = new DataView(blockhashHashBytes.buffer, blockhashHashBytes.byteOffset, blockhashHashBytes.byteLength);
    const blockhashHashNumber = typeof blockhashHash === 'bigint' 
      ? Number(blockhashHash) 
      : typeof blockhashHash === 'string' 
      ? Number(blockhashHash)
      : blockhashHash;
    dataView.setUint32(0, blockhashHashNumber, true); // true = little endian, u32 (4 bytes)

    return PublicKey.findProgramAddressSync(
      [stringToBytes('vault'), buyerBytes, blockhashHashBytes, mint.toBuffer()],
      this.programId,
    );
  }

  /**
   * Get Extra Token Vault PDA (DEPRECATED - for backward compatibility)
   * Seeds: ["vault", groupId (u64 le), mint]
   * 
   * @deprecated This method is deprecated. Use `getExtraTokenVaultPDA(buyer, blockhashHash, mint)` instead.
   * This method was used for the old PDA pattern [b"vault", group_id, mint], but we now exclusively
   * use the buy_tickets_auto pattern [b"vault", buyer, blockhash_hash, mint] for all vault operations.
   * 
   * This method is kept for backward compatibility only.
   * 
   * @param groupId - Can be number or bigint (supports large numbers > Number.MAX_SAFE_INTEGER)
   */
  getExtraTokenVaultPDAOld(groupId: number | bigint, mint: PublicKey): [PublicKey, number] {
    if (process.env.NODE_ENV !== 'production') {
      console.warn(
        '[DEPRECATED] getExtraTokenVaultPDAOld is deprecated. Use getExtraTokenVaultPDA(buyer, blockhashHash, mint) instead. ' +
        'This method is only kept for backward compatibility.'
      );
    }
    const groupIdBuffer = writeUInt64LE(groupId);
    return PublicKey.findProgramAddressSync(
      [stringToBytes('vault'), groupIdBuffer, mint.toBuffer()],
      this.programId,
    );
  }

  /**
   * Get Collection Mint PDA
   * Seeds: ["collection_mint"]
   */
  getCollectionMintPDA(): [PublicKey, number] {
    return PublicKey.findProgramAddressSync(
      [stringToBytes('collection_mint')],
      this.programId,
    );
  }

  /**
   * Get Authority PDA
   * Seeds: ["authority"]
   */
  getAuthorityPDA(): [PublicKey, number] {
    return PublicKey.findProgramAddressSync(
      [stringToBytes('authority')],
      this.programId,
    );
  }

  /**
   * Get all PDAs for a group (DEPRECATED)
   * @deprecated This method is deprecated. Use `getGroupStatePDAForAuto(buyer, blockhashHash)` directly instead.
   * @param groupId - Can be number or bigint (supports large numbers > Number.MAX_SAFE_INTEGER)
   */
  getGroupPDAs(groupId: number | bigint) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn(
        '[DEPRECATED] getGroupPDAs is deprecated. Use getGroupStatePDAForAuto(buyer, blockhashHash) directly instead.'
      );
    }
    return {
      groupState: this.getGroupStatePDA(groupId),
    };
  }

  /**
   * Get all global PDAs
   */
  getGlobalPDAs() {
    return {
      globalState: this.getGlobalStatePDA(),
      jackpotVault: this.getJackpotVaultPDA(),
      collectionMint: this.getCollectionMintPDA(),
      authority: this.getAuthorityPDA(),
    };
  }
}

// Export singleton instance
export const solanaPDA = new SolanaPDA();
